import { IosShare } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { PolicyDownloadButton } from "~/components/policy-download-button";
import { Flex } from "~/components/Flex";
import { TabsVariant, VariantIcon } from "~/components/ui-library";
import { copyToClipboard } from "~/lib/clipboard";
import { PolicyIdToMrn, PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { TrustLevel, useGetPolicyQuery } from "~/operations";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { ShowMoreSummary } from "~/components/ShowMore/ShowMoreSummary";
import { setDocumentTitle } from "~/lib/commonUtils";
import { PolicyDeleteButton } from "~/components/policy-gql/PolicyDeleteButton";
import { CanDeletePolicy } from "~/lib/iam/actions/cnspec/policy/PolicyHub/DeletePolicy";
import { CanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import { EmailIcon, LinkIcon } from "~/icons";
import { Caption } from "~/pages/security/policies/components/Caption";
import { PolicyAssignButton } from "~/pages/security/policies/components/PolicyAssignButton";
import { PolicyAssignScope } from "./utils";

export type PoliciesAddDetailPageProps = {
  scope: PolicyAssignScope;
  basePath: string;
  sectionLabel: string;
  renderBreadcrumbs: (policyName: string) => ReactNode;
};

export function PoliciesAddDetailPage({
  scope,
  basePath,
  sectionLabel,
  renderBreadcrumbs,
}: PoliciesAddDetailPageProps) {
  const { policyId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const shareOpen = Boolean(shareAnchorEl);

  const mrn = PolicyIdToMrn(policyId);
  const selectedVariantId = searchParams.get("variant");

  const { data, loading, error, refetch } = useGetPolicyQuery({
    variables: { input: { mrn, spaceMrn: scope.mrn } },
    skip: !policyId,
  });

  const loadingFailed = !policyId || error || !data?.policy;
  const policy = data?.policy;
  const isCustom = policy?.trustLevel === TrustLevel.Private;

  const variants = [
    {
      label: "Overall",
      value: "",
      icon: "",
    },
    ...(
      policy?.variantPlatformFilters?.map((variant) => ({
        label: variant?.title || "",
        value: variant?.id || "",
        icon: <VariantIcon type={variant?.icon || ""} />,
      })) || []
    ).sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];

  const generateHref = (tab: string): string => {
    return `${basePath}/add/${PolicyMrnToURIEncodedId(policy?.mrn || "")}/${tab}`;
  };

  const tabList: TabListItem[] = [
    {
      label: "Checks",
      to: generateHref("checks"),
      route: "/checks",
    },
  ];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "checks",
  );

  const shareLink = window.location.href;
  const shareEmailLink = `mailto:hello@mondoo.com?subject=${encodeURIComponent("Take a look at this policy")}&body=${encodeURIComponent(shareLink)}`;

  setDocumentTitle([policy?.name, "Add Policies", sectionLabel, "Security"]);

  return (
    <Box>
      {renderBreadcrumbs(policy?.name || "")}
      {policy && (
        <>
          <Box
            id="policy-header"
            mb={3}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Flex flexDirection="column">
              <Typography
                variant="h4"
                component="h1"
                fontWeight={700}
                textTransform="uppercase"
                sx={{ mb: 1 }}
              >
                {policy.name}
              </Typography>
              <Caption
                trustLevel={policy.trustLevel}
                version={policy.version}
                authors={policy.authors}
              />
            </Flex>
            <Flex id="policy-actions" alignItems="center" gap={4}>
              <Tooltip title="Share" placement="top" arrow>
                <IconButton onClick={(e) => setShareAnchorEl(e.currentTarget)}>
                  <IosShare />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={shareAnchorEl}
                open={shareOpen}
                onClose={() => setShareAnchorEl(null)}
                onClick={() => setShareAnchorEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <MenuItem onClick={() => copyToClipboard(shareLink)}>
                  <ListItemIcon>
                    <LinkIcon fontSize="inherit" />
                  </ListItemIcon>
                  <ListItemText>Copy link</ListItemText>
                </MenuItem>
                <MenuItem component="a" href={shareEmailLink}>
                  <ListItemIcon>
                    <EmailIcon fontSize="inherit" />
                  </ListItemIcon>
                  <ListItemText>Email</ListItemText>
                </MenuItem>
              </Menu>
              <PolicyDownloadButton policy={policy} size="small" iconOnly />
              <Divider orientation="vertical" flexItem />
              {isCustom && (
                <CanDeletePolicy>
                  <PolicyDeleteButton
                    space={scope}
                    policyMrn={policy.mrn}
                    isCustom={isCustom}
                  />
                </CanDeletePolicy>
              )}
              <CanAssign>
                <PolicyAssignButton
                  scope={scope}
                  policyMrn={policy.mrn}
                  assigned={policy.assigned}
                  action={policy.action}
                  onChange={() => {
                    refetch();
                  }}
                  advanced
                />
              </CanAssign>
            </Flex>
          </Box>
          {variants.length > 1 && (
            <TabsVariant
              options={variants}
              selectedTab={selectedVariantId || ""}
              onTabChange={(variantId) => {
                variantId.length > 0
                  ? searchParams.set("variant", variantId)
                  : searchParams.delete("variant");
                setSearchParams(searchParams);
              }}
              mb={3}
            />
          )}
          {policy.docs && (
            <ShowMoreSummary
              maxHeight={72}
              text={policy.docs}
              externalLinksInNew
            />
          )}
        </>
      )}

      {loading && <LoadingPage what="Policy" />}
      {!loading && loadingFailed && <LoadingFailedPage what="Policy" />}
      {!loading && policy && (
        <Box>
          <TabNavigation {...{ id: "policy-tabs", tabList, currentTab }} />
          <Outlet
            context={{
              scope,
              policyId,
              variantPlatformFilter: selectedVariantId,
            }}
          />
        </Box>
      )}
    </Box>
  );
}
